import { createThunkAction } from 'admin/actions/helpers';
import { auth as authEndpoints } from 'admin/resources';
import queryClient from 'admin/queries/client';
import { organizationKeys } from 'admin/queries/queryKeys';
import {
	GET_TOTP,
	LOGIN,
	SWITCH_ORG,
	POST_TOTP,
	POST_TOTP_RECOVERY_CODES,
	POST_TOTP_REMOVE,
	POST_TOTP_REQUIREMENT,
	POST_TOTP_VERIFY,
} from 'admin/constants/actions';
import type { IUserOrganization } from 'src/types';
import { ADMIN_PASSWORD_SESSION, ADMIN_SESSION, ADMIN_SSO_SESSION } from 'common/constants';

export type LoginResult = {
	twofactor: boolean;
};

export interface GetTOTPResult {
	enabled: boolean;
	phone: string | null;
	totp: {
		verified: boolean;
		recoveryCodes: string[];
		type: 'app';
		url: string;
	};
}

export interface PostTOTPResult {
	message: string;
}

export type PostTOTPVerifyResult = string;
export type PostTOTPRequirementResult = { required: boolean; enabled: boolean };
export type PostTOTPRecoveryCodesResult = string;
export type PostTOTPRemovalResult = string;

export const auth = {
	get: {
		totp: createThunkAction<GetTOTPResult, void>({
			type: GET_TOTP,
			payloadCreator: async (params, { dispatch }) => {
				const res = await authEndpoints.get.totp.send({});
				return { success: true, result: res.body };
			},
		}),
	},
	post: {
		login: createThunkAction<
			LoginResult,
			{ organizationSlug: IUserOrganization['customId']; email: string; password: string; code?: string }
		>({
			type: LOGIN,
			payloadCreator: async ({ organizationSlug, ...params }) => {
				const res = await authEndpoints.post.login.params({ organizationSlug }).send(params);
				if (!res.body?.twofactor) {
					const sessionId = localStorage.getItem(ADMIN_SESSION);
					if (sessionId) localStorage.setItem(ADMIN_PASSWORD_SESSION, sessionId);
					localStorage.removeItem(ADMIN_SSO_SESSION);
				}
				return { success: true, result: res.body };
			},
		}),
		switchOrg: createThunkAction<unknown, { organizationSlug: IUserOrganization['customId'] }>({
			type: SWITCH_ORG,
			payloadCreator: async ({ organizationSlug }) => {
				const res = await authEndpoints.post.switchOrg.params({ organizationSlug }).send();

				queryClient.invalidateQueries({
					queryKey: organizationKeys.auditLogs.all(),
				});

				return { success: true, result: res.body };
			},
		}),
		totp: createThunkAction<PostTOTPResult, { type: 'app' }>({
			type: POST_TOTP,
			payloadCreator: async params => {
				const res = await authEndpoints.post.totp.send(params);
				return { success: true, result: res.body };
			},
		}),
		totpRequirement: createThunkAction<PostTOTPRequirementResult, void>({
			type: POST_TOTP_REQUIREMENT,
			payloadCreator: async params => {
				const res = await authEndpoints.post.totpRequirement.send();
				return { success: true, result: res.body };
			},
		}),
		totpVerify: createThunkAction<PostTOTPVerifyResult, { code: string }>({
			type: POST_TOTP_VERIFY,
			payloadCreator: async params => {
				const res = await authEndpoints.post.totpVerify.send(params);
				return { success: true, result: res.body };
			},
		}),
		totpRecoveryCodes: createThunkAction<PostTOTPRecoveryCodesResult, { code: string; password: string }>({
			type: POST_TOTP_RECOVERY_CODES,
			payloadCreator: async params => {
				const res = await authEndpoints.post.totpRecoveryCodes.send(params);
				return { success: true, result: res.body };
			},
		}),
		totpRemove: createThunkAction<PostTOTPRemovalResult, { code: string; password: string }>({
			type: POST_TOTP_REMOVE,
			payloadCreator: async params => {
				const res = await authEndpoints.post.totpRemove.send(params);
				return { success: true, result: res.body };
			},
		}),
	},
};
